import { useIsLayersDisabledWithReason } from "#webapp/src/components/chat/ai-chat/components/multi-calendar/calendar-layers.util";
import { ToggleCalendarLayers } from "#webapp/src/components/chat/ai-chat/components/multi-calendar/ToggleCalendarLayers";
import useOrgDomains from "#webapp/src/components/hooks/useOrgDomains";
import { PopoverWrapper } from "#webapp/src/components/web-app-calendar/calendar-popover/PopoverWrapper";
import { useReadCalendarColors } from "#webapp/src/components/web-app-calendar/hooks/CalendarColorsContext";
import {
  addCalendarIds,
  removeCalendarIds,
} from "#webapp/src/state/actions/multi-calendar.actions";
import { IReduxState } from "#webapp/src/state/reducers/root.reducer";
import { isEmailInDomains } from "@clockwise/client-commons/src/util/email";
import { Button } from "@clockwise/design-system";
import { border_default } from "@clockwise/design-system/tokens";
import { ExpandMore } from "@clockwise/icons";
import { AttendeeAvatar } from "@clockwise/web-commons/src/ui/AttendeeAvatar";
import { useEcosystem } from "@clockwise/web-commons/src/util/ecosystem";
import { getFullName } from "@clockwise/web-commons/src/util/profile.util";
import classNames from "classnames";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Attendee, toEventCardAttendee } from "../../proposals/utils/attendee";
import { EventCardAttendee } from "../../types";
import { CannotViewGuesListMessage } from "../ECAttendeesV2/CannotViewGuesListMessage";
import { ECAttendeeList } from "../ECAttendeesV2/ECAttendeeList";
import { ECAttendeeSelect } from "../ECAttendeesV2/ECAttendeeSelect";
import { getListOfNonRemovableAttendeeIds } from "../ECAttendeesV2/ECAttendeeWrapper";
import { OmittedAttendeesMessage } from "../ECAttendeesV2/OmittedAttendeesMessage";

const toAttendee = (eventCardAttendee: EventCardAttendee): Attendee => {
  const displayName =
    eventCardAttendee.profile?.givenName && eventCardAttendee.profile?.familyName
      ? getFullName(eventCardAttendee.profile)
      : eventCardAttendee.primaryCalendar;

  return {
    id: eventCardAttendee.id,
    isOptional: !!eventCardAttendee.isOptional,
    user: eventCardAttendee.userId ? { id: eventCardAttendee.userId } : null,
    isOrganizer: eventCardAttendee.isOrganizer ?? null,
    responseStatus: eventCardAttendee.attendingStatus ?? null,
    person: {
      id: eventCardAttendee.primaryCalendar,
      email: eventCardAttendee.primaryEmail,
      displayName,
      givenName: eventCardAttendee.profile?.givenName ?? null,
      familyName: eventCardAttendee.profile?.familyName ?? null,
      externalImageUrl: eventCardAttendee.profile?.externalImageUrl ?? null,
      isMe: eventCardAttendee.isYou,
    },
  };
};

export const TimeSuggestionsAttendeePicker = ({
  attendees,
  attendeesOmitted,
  readOnly,
  loading = false,
  permissions,
  onRemoveAttendee,
  onAddAttendee,
  onUpdateAttendeeOptionality,
}: {
  attendees: Attendee[];
  permissions: {
    canModify: boolean;
    canInviteOthers: boolean;
    canSeeOtherGuests: boolean;
  };
  readOnly: boolean;
  attendeesOmitted: boolean;
  loading?: boolean;
  onRemoveAttendee: (email: string) => void;
  onAddAttendee: (attendee: Attendee) => void;
  onUpdateAttendeeOptionality: (email: string, isOptional: boolean) => void;
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

  const ecosystem = useEcosystem();
  const { domains: orgDomains, loading: domainsLoading } = useOrgDomains();
  const calendarColorMap = useReadCalendarColors();

  const eventCardAttendees = attendees.map(toEventCardAttendee);
  const attendeeCalendarIds = attendees.map((a) => a.person.id);

  const calendarIdsToFullyShow = useSelector(
    (state: IReduxState) => state.multiCalendar.calendarIdsToFullyShow,
  );
  const isToggledOn = attendeeCalendarIds.every((id) => calendarIdsToFullyShow.includes(id));
  const dispatch = useDispatch();
  const { disabled, reason: reasonForDisabled } = useIsLayersDisabledWithReason(
    attendeeCalendarIds,
  );

  const attendeesLoading = loading;

  const nonEditableAttendeeIds = getListOfNonRemovableAttendeeIds(
    permissions.canInviteOthers,
    permissions.canModify,
    eventCardAttendees,
    ecosystem,
  );

  const handleToggleCalendarLayers = () => {
    if (isToggledOn) {
      dispatch(removeCalendarIds(attendeeCalendarIds));
    } else {
      dispatch(addCalendarIds(attendeeCalendarIds));
    }
  };

  const handleRemoveAttendee = (email: string) => {
    onRemoveAttendee?.(email);
    dispatch(removeCalendarIds([email]));
  };

  const handleUpdateAttendeeOptionality = (attendee: EventCardAttendee, isOptional: boolean) => {
    onUpdateAttendeeOptionality?.(attendee.primaryEmail, isOptional);
  };

  const handleAddAttendee = (attendee: EventCardAttendee) => {
    onAddAttendee?.(toAttendee(attendee));
    dispatch(addCalendarIds([attendee.primaryCalendar]));
  };

  const eventAttendeesRef = React.useRef(attendees);
  React.useEffect(() => {
    eventAttendeesRef.current = attendees;
  }, [attendees]);
  React.useEffect(() => {
    // on unmount we want to remove all the attendees calendars of this event
    return () => {
      // Dev Note: We require a ref to keep track of the previous eventAttendees
      // If we use `eventAttendees` as a dependency, it'll always trigger this cleanup
      // But we only want it to trigger when the component is unmounted
      // And we want to trigger it with the latest eventAttendees
      // Thus a ref is used to keep track of the latest eventAttendees
      dispatch(removeCalendarIds(eventAttendeesRef.current.map((a) => a.person.email)));
    };
  }, []);

  if (attendeesLoading) {
    return "loading...";
  }

  return (
    <div className={classNames("cw-flex cw-items-center cw-justify-between cw-w-full")}>
      <Button
        variant="outlined"
        size="xsmall"
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
        endIcon={ExpandMore}
      >
        {eventCardAttendees.map((attendee) => {
          const isExternal =
            Boolean(orgDomains.length) &&
            !domainsLoading &&
            !isEmailInDomains(attendee.primaryCalendar, orgDomains);
          const calendarColors = calendarColorMap[attendee.primaryCalendar];
          const isIncludedInMultiCalendar = !!calendarIdsToFullyShow.includes(
            attendee.primaryCalendar,
          );
          const attendeeBorderColor =
            calendarColors && isIncludedInMultiCalendar ? calendarColors.border : border_default;
          return (
            <AttendeeAvatar
              iconType={attendee.attendingStatus}
              key={attendee.primaryCalendar}
              profile={
                attendee.profile || {
                  familyName: attendee.primaryCalendar || null,
                  givenName: null,
                  externalImageUrl: null,
                }
              }
              isOrganizer={attendee.isOrganizer || false}
              isExternalUser={isExternal}
              borderColor={attendeeBorderColor}
              size="large"
            />
          );
        })}
      </Button>
      <PopoverWrapper
        id="cw-menu-suggestions-attendee-picker"
        anchorEl={anchorEl ? anchorEl : null}
        onClose={() => setAnchorEl(null)}
        verticalPlacement="bottom"
        side="left"
      >
        <div className={classNames("cw-p-1")}>
          <ECAttendeeSelect
            disabled={readOnly}
            currentAttendees={eventCardAttendees}
            onAddAttendee={handleAddAttendee}
          />
          {!permissions.canSeeOtherGuests && <CannotViewGuesListMessage />}
          <ECAttendeeList
            attendees={eventCardAttendees}
            nonEditableAttendeeIds={nonEditableAttendeeIds}
            removeAttendee={handleRemoveAttendee}
            changeOptionalityOfAttendee={handleUpdateAttendeeOptionality}
            readOnly={readOnly}
          />
          {attendeesOmitted && <OmittedAttendeesMessage />}
        </div>
      </PopoverWrapper>
      <ToggleCalendarLayers
        onClick={handleToggleCalendarLayers}
        disabled={attendeeCalendarIds.length <= 1 || disabled}
        isToggledOn={isToggledOn}
        reasonForDisabled={reasonForDisabled}
      />
    </div>
  );
};
